import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { SectionEyebrow } from '@/components/ui/SectionEyebrow'
import { Text } from '@/components/ui/Text'

import { Day0UploadDropzone } from './Day0UploadDropzone'
import { Button } from '@/components/ui/Button'

interface StageLink {
  label: string
  url: string
}

interface Day0NextStepCardProps {
  /** null when BE gap — heading element omitted (same pattern as instructionBody) */
  instructionHeading: string | null
  /** null when BE gap — body section omitted */
  instructionBody: string | null
  /** null → dropzone renders with fallback label */
  primaryAction: string | null
  /** null → secondary footer section hidden */
  secondaryAction: string | null
  /** null → secondary button is aria-disabled */
  secondaryUrl: string | null
  /** null/empty → tertiary links section omitted */
  tertiaryLinks: StageLink[] | null
  /** null/empty → format chips replaced by generic label */
  acceptedFormats: string[] | null
  /** When provided, dropzone is enabled and calls this on file pick/drop */
  onFile?: (file: File) => void
  /** Extra content rendered inside the card below the instruction body (e.g., entity review at stage 3) */
  children?: React.ReactNode
  /**
   * True for a held / fallback state (nothing actionable, the copy explains why): the heading and body are wrapped in a
   * polite live region so a change (e.g. AWAITING_COUNTERSIGNATURE → ADVANCING) is announced. The wrapper holds TEXT only.
   */
  statusRole?: boolean
}

export function Day0NextStepCard({
  instructionHeading,
  instructionBody,
  primaryAction,
  secondaryAction,
  secondaryUrl,
  tertiaryLinks,
  acceptedFormats,
  onFile,
  children,
  statusRole = false,
}: Day0NextStepCardProps) {
  return (
    <Card data-testid="day0-next-step-card">
      <CardHeader className="flex-row items-center py-4">
        <SectionEyebrow>WHAT TO DO NOW</SectionEyebrow>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-5">
          <div
            role={statusRole ? 'status' : undefined}
            className={statusRole ? 'flex flex-col gap-5' : 'contents'}
          >
            {instructionHeading && (
              <Text variant="headline-sm" className="max-w-prose">
                {instructionHeading}
              </Text>
            )}

            {instructionBody && (
              <Text variant="body-md" color="muted" className="max-w-prose">
                {instructionBody}
              </Text>
            )}
          </div>

          {onFile !== undefined && (
            <Day0UploadDropzone
              label={primaryAction ?? 'Upload'}
              acceptedFormats={acceptedFormats ?? []}
              onFile={onFile}
            />
          )}

          {children}

          {secondaryAction && (
            <div className="flex items-center justify-between rounded-md bg-surface-muted px-4 py-3">
              <Text variant="body-sm" color="muted">
                {secondaryAction}
              </Text>
              <Button
                variant="outline"
                size="sm"
                aria-disabled={!secondaryUrl ? 'true' : undefined}
                onClick={(e) => {
                  if (!secondaryUrl) e.preventDefault()
                }}
              >
                Request
              </Button>
            </div>
          )}

          {tertiaryLinks && tertiaryLinks.length > 0 && (
            <div className="flex gap-4">
              {tertiaryLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.url}
                  className="text-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded"
                >
                  {link.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
